import _ from 'lodash';
import formatters from '../formatters';
import { NullableOption, Stringifiable } from '../utilityTypes';
import { useFuzzySearch } from './fuzzySearch';

function search<T extends Stringifiable = string>(
    items: T[],
    term: string,
    getter: (a: T) => Stringifiable = _.identity
): T[] {
    const loweredCaseTerm = formatters.normalize(term);
    return items.filter(item =>
        formatters.normalize(getter(item).toString()).includes(loweredCaseTerm)
    );
}

function keyBy<T, K extends number | string>(
    array: T[] | T,
    mapper: (obj: T) => K
): Record<K, T> {
    return _.keyBy(Array.isArray(array) ? array : [array], mapper) as Record<
        K,
        T
    >;
}

function pagination(page: number, size: number) {
    return <T>(collection: T[]): [T[], number] => {
        const chunk = collection.slice((page - 1) * size, page * size);
        const lastPage =
            collection.length !== 0 ? Math.ceil(collection.length / size) : 1;
        return [chunk, lastPage];
    };
}

const chainCoalescing = <Item>(
    options: NullableOption<Item>[],
    defaultItem: Item
): Item =>
    options.reduce(
        (previous: NullableOption<Item>, current: NullableOption<Item>) => {
            return previous ?? current;
        }
    ) ?? defaultItem;

function concat<T>(head: T | T[], ...rest: (T | T[])[]): T[] {
    return [head, ...rest]
        .map(x => (Array.isArray(x) ? x : [x]))
        .reduce(Array.prototype.concat);
}

const arrayIncludes = <T extends U, U>(
    coll: ReadonlyArray<T>,
    el: U
): el is T => {
    return coll.includes(el as T);
};

const isLast = (index: number, totalEntries: number): boolean =>
    totalEntries === 0 || index === totalEntries - 1;

const isFirst = (index: number): boolean => index === 0;

const boolAnd = (...args: unknown[]): boolean => {
    return args.reduce((previous: boolean, current: unknown) => {
        return Boolean(previous) && Boolean(current);
    }, true);
};

export {
    arrayIncludes,
    useFuzzySearch,
    chainCoalescing,
    concat,
    isFirst,
    isLast,
    keyBy,
    pagination,
    search,
    boolAnd,
};
